import React, { useState, useEffect } from 'react';
import { formatLocalDate } from '../utils/timeUtils';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { IconSwords } from './Icons';
import AddMatchesModal from './seasons/AddMatchesModal';

// embedded: shown as the Matches tab of the season page (no own back button / title)
function SeasonMatches({ embedded = false, seasonId } = {}) {
  const { t } = useTranslation();
  const params = useParams();
  const id = seasonId != null ? String(seasonId) : params.id;
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [reload, setReload] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(null); // match id with a sync / remove running
  const [toast, setToast] = useState(null); // { text, err }

  const say = (text, err = false) => { setToast({ text, err }); setTimeout(() => setToast(cur => (cur && cur.text === text ? null : cur)), 5000); };
  const errText = (e) => (e.response && e.response.data && e.response.data.error) || e.message;

  // re-copy the season's current settings onto one of its matches
  const syncMatch = async (e, m) => {
    e.preventDefault(); e.stopPropagation();
    setBusy(m.id);
    try {
      const r = await axios.post(`/api/seasons/${id}/matches/${m.id}/sync`);
      say(r.data.synced && r.data.synced.length
        ? t('seasonMatches.synced', 'Match #{{id}} synced with the season', { id: m.id })
        : t('seasonMatches.syncedLink', 'Match #{{id}} is {{status}}: only linked to the season', { id: m.id, status: t(`matchStatus.${m.status}`, m.status) }));
    } catch (err) { say(errText(err), true); } finally { setBusy(null); }
  };
  const removeMatch = async (e, m) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(t('seasonMatches.removeConfirm', 'Take match #{{id}} out of this season? Its settings stay as they are; season temp admins lose their rights in it.', { id: m.id }))) return;
    setBusy(m.id);
    try {
      await axios.delete(`/api/seasons/${id}/matches/${m.id}`);
      say(t('seasonMatches.removed', 'Match #{{id}} removed from the season', { id: m.id }));
      setReload(n => n + 1);
    } catch (err) { say(errText(err), true); } finally { setBusy(null); }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [seasonRes, matchesRes] = await Promise.all([
          axios.get(`/api/seasons`),
          axios.get(`/api/seasons/${id}/matches`),
        ]);
        const found = (seasonRes.data || []).find(s => s.id === parseInt(id));
        setSeason(found || null);
        setMatches(matchesRes.data || []);
      } catch (err) {
        console.error('Failed to load season matches:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, reload]);

  const addButton = (
    <button type="button" onClick={() => setAddOpen(true)} title={t('seasonMatches.addHintShort', 'Put matches created without this season into it')}
      style={{
        padding: '3px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: 600, fontSize: '0.7rem', marginRight: '0.4rem',
        background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', color: 'var(--accent-green)',
      }}>+ {t('seasonMatches.addExisting', 'Add matches')}</button>
  );
  const toastEl = toast && <span className={`sp-toast${toast.err ? ' err' : ''}`}>{toast.text}</span>;

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(matches.length / pageSize);
  const displayedMatches = pageSize === 'all' ? matches : matches.slice((page - 1) * pageSize, page * pageSize);

  const statusColors = {
    pending: { bg: 'rgba(245,158,11,0.06)', border: 'rgba(245,158,11,0.15)', color: 'var(--accent-amber)' },
    live: { bg: 'rgba(34,197,94,0.06)', border: 'rgba(34,197,94,0.15)', color: 'var(--accent-green)' },
    finished: { bg: 'rgba(59,130,246,0.06)', border: 'rgba(59,130,246,0.15)', color: 'var(--accent-blue)' },
    cancelled: { bg: 'rgba(239,68,68,0.06)', border: 'rgba(239,68,68,0.15)', color: 'var(--accent-red)' },
  };

  const pageSizeButtons = [10, 50, 'all'].map(size => (
    <button
      key={size}
      onClick={() => { setPageSize(size); setPage(1); }}
      style={{
        padding: '3px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: 500, fontSize: '0.7rem',
        background: pageSize === size ? 'rgba(59,130,246,0.12)' : 'transparent',
        border: pageSize === size ? '1px solid rgba(59,130,246,0.25)' : '1px solid var(--border-subtle)',
        color: pageSize === size ? 'var(--accent-blue)' : 'var(--text-muted)',
        transition: 'all 0.15s', textTransform: 'uppercase', letterSpacing: '0.03em',
      }}
    >
      {size === 'all' ? t('common.all', 'All') : size}
    </button>
  ));

  return (
    <div>
      {embedded ? (
        <div className="sp-embedded-bar">
          {!loading && <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginRight: 'auto' }}>{t('seasonMatches.matchCount', '{{count}} matches', { count: matches.length })}</span>}
          {toastEl}
          {addButton}
          {pageSizeButtons}
        </div>
      ) : (
      /* Season Info Header Card */
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.75rem', marginBottom: '1rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            onClick={() => navigate('/admin/seasons')}
            style={{
              padding: '0.3rem 0.7rem', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem',
              background: 'transparent', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)',
              transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '0.3rem',
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = 'var(--border-hover)'}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
          >← {t('nav.seasons', 'Seasons')}</button>
          <div style={{ width: 1, height: 24, background: 'var(--border-subtle)' }} />
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }}>
              {season ? season.name : t('seasonMatches.season', 'Season')} - {t('nav.matches', 'Matches')}
            </h2>
            {!loading && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                {t('seasonMatches.matchCount', '{{count}} matches', { count: matches.length })}
                {season && season.best_of ? ` · BO${season.best_of}` : ''}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          {toastEl}
          {addButton}
          {pageSizeButtons}
        </div>
      </div>
      )}

      {/* Matches List */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.75rem',
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{t('seasonMatches.loading', 'Loading matches...')}</div>
        ) : matches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3.5rem 1rem', color: 'var(--text-muted)' }}>
            <div style={{ marginBottom: '0.75rem' }}><IconSwords size={32} color="var(--text-muted)" /></div>
            <div style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>{t('seasonMatches.noMatches', 'No matches yet')}</div>
            <div style={{ fontSize: '0.75rem' }}>{t('seasonMatches.noMatchesHint', 'Matches created with this season preset will appear here')}</div>
            <div style={{ marginTop: '0.9rem' }}>{addButton}</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              {displayedMatches.map(m => {
                const sc = statusColors[m.status] || statusColors.pending;

                return (
                  <Link
                    key={m.id}
                    to={`/admin/matches/${m.id}`}
                    style={{
                      background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)', padding: '0.75rem 1.25rem', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '1rem',
                      transition: 'border-color 0.15s, background 0.15s',
                      textDecoration: 'none', color: 'inherit',
                    }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--border-hover)'; e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                  >
                    {/* Match ID */}
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500, fontFamily: 'var(--font-mono)', flexShrink: 0, minWidth: 32 }}>
                      #{m.id}
                    </div>

                    {/* Teams */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {m.team1_name || 'Team 1'} <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.75rem' }}>vs</span> {m.team2_name || 'Team 2'}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                        {m.server_country_code && (
                          <img
                            src={`https://flagcdn.com/14x10/${m.server_country_code.toLowerCase()}.png`}
                            alt={m.server_country_code}
                            style={{ width: 14, height: 10, borderRadius: 1 }}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        )}
                        {m.map || 'TBD'}{m.best_of > 1 ? ` · BO${m.best_of}` : ''}{m.server_name ? ` · ${m.server_name}` : ''} · {formatLocalDate(m.created_at)}
                      </div>
                    </div>

                    {/* Score */}
                    {(m.status === 'finished' || m.status === 'live') && (
                      <div style={{
                        fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', flexShrink: 0,
                        background: 'rgba(255,255,255,0.02)', padding: '0.25rem 0.6rem', borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border-subtle)',
                      }}>
                        {m.best_of > 1 ? `${m.map_wins_team1 || 0} - ${m.map_wins_team2 || 0}` : `${m.score_team1 || 0} - ${m.score_team2 || 0}`}
                      </div>
                    )}

                    {/* Status Badge */}
                    <span style={{
                      padding: '0.2rem 0.55rem', borderRadius: 'var(--radius-sm)', fontSize: '0.72rem', fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0,
                      background: sc.bg, border: `1px solid ${sc.border}`, color: sc.color,
                    }}>{t(`matchStatus.${m.status}`, m.status)}</span>

                    {/* Sync with the season / take out of it (bracket matches belong to their bracket's season) */}
                    <span className="sp-mrow-actions">
                      <button type="button" className="sp-mrow-btn" disabled={busy === m.id} onClick={e => syncMatch(e, m)}
                        title={t('seasonMatches.syncHint', "Copy the season's current settings onto this match again")}>{t('seasonMatches.sync', 'Sync')}</button>
                      {!m.bracket_match_id && (
                        <button type="button" className="sp-mrow-btn danger" disabled={busy === m.id} onClick={e => removeMatch(e, m)}
                          title={t('seasonMatches.removeHint', 'Take this match out of the season')}>{t('seasonMatches.remove', 'Remove')}</button>
                      )}
                    </span>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            {pageSize !== 'all' && matches.length > pageSize && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1.25rem' }}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  style={{
                    padding: '4px 12px', borderRadius: 'var(--radius-sm)', cursor: page <= 1 ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: '0.75rem',
                    background: 'transparent', border: '1px solid var(--border-subtle)', color: page <= 1 ? 'var(--text-muted)' : 'var(--text-secondary)',
                    transition: 'all 0.15s',
                  }}
                >{t('seasonMatches.prev', 'Prev')}</button>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {t('seasonMatches.pageOf', 'Page {{page}} of {{total}}', { page, total: totalPages })}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  style={{
                    padding: '4px 12px', borderRadius: 'var(--radius-sm)', cursor: page >= totalPages ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: '0.75rem',
                    background: 'transparent', border: '1px solid var(--border-subtle)', color: page >= totalPages ? 'var(--text-muted)' : 'var(--text-secondary)',
                    transition: 'all 0.15s',
                  }}
                >{t('common.next', 'Next')}</button>
              </div>
            )}
          </>
        )}
      </div>

      <AddMatchesModal isOpen={addOpen} onClose={() => setAddOpen(false)} seasonId={id} seasonName={season && season.name}
        onAdded={(r, allOk) => {
          setReload(n => n + 1);
          if (r.added) say(t('seasonMatches.addedN', '{{count}} match(es) added and synced with the season', { count: r.added }));
          if (allOk) setAddOpen(false);
        }} />
    </div>
  );
}

export default SeasonMatches;
